/*
 * cart-property-sync.js
 *
 * Subscribes to PUB_SUB_EVENTS.cartUpdate and remediates missing line item
 * properties for special product types:
 *   - Play Subscription   (_playSub, _fcID, _shopifyCustomerID)
 *   - Studio Pro          (_studioPro, _fcID, _shopifyCustomerID)
 *   - Pre-Order           (_preOrder, _preOrderDate, _preOrderLabel, [label])
 *   - Back-Order          (_backOrder, _backOrderDate, _backOrderLabel, [label])
 *   - Made-to-Order       (treated as Pre-Order)
 *
 * Product/variant metafield data is sourced from /pages/cart-data, whose URL
 * is read from [data-cart-data-url] on #cart-init-data.
 *
 * Customer identity for digital products is read from #react-jwt-all
 * (data-customerid = Fender Connect ID, data-shopifycustomer = Shopify ID).
 */
(function () {
  if (typeof window === 'undefined') return;

  const PLAY_SUB = 'play_subscription';
  const STUDIO_PRO = 'studio_pro';

  let inFlight = false;
  let pendingRerun = false;

  function readJwtIds() {
    const el = document.getElementById('react-jwt-all');
    if (!el) return { fcID: '', shopifyCustomerID: '' };
    return {
      fcID: el.getAttribute('data-customerid') || '',
      shopifyCustomerID: el.getAttribute('data-shopifycustomer') || ''
    };
  }

  function getCartDataUrl() {
    const init = document.getElementById('cart-init-data');
    return (init && init.getAttribute('data-cart-data-url')) || '/pages/cart-data';
  }

  async function fetchCartData() {
    const res = await fetch(getCartDataUrl(), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    if (!res.ok) throw new Error('cart-data fetch failed: ' + res.status);
    return res.json();
  }

  async function fetchLiveCart() {
    const res = await fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    if (!res.ok) throw new Error('/cart.js fetch failed: ' + res.status);
    return res.json();
  }

  function matchCartItem(cartItem, dataItem) {
    if (!cartItem || !dataItem) return false;
    if (cartItem.key && dataItem.key && cartItem.key === dataItem.key) return true;
    return String(cartItem.variant_id) === String(dataItem.variant_id);
  }

  function buildRequiredProperties(cartItem, dataItem, ids) {
    const existing = cartItem.properties || {};
    const variantMf = (dataItem.metafields && dataItem.metafields.variant) || {};
    const productMf = (dataItem.metafields && dataItem.metafields.product) || {};

    const digital = (productMf.digital_product || '').toString();
    const isPlaySub = digital === PLAY_SUB;
    const isStudioPro = digital === STUDIO_PRO;

    const isMadeToOrder =
      variantMf.made_to_order === true || variantMf.made_to_order === 'true';
    const isPreOrder =
      variantMf.available_for_pre_order === true ||
      variantMf.available_for_pre_order === 'true' ||
      isMadeToOrder;
    const isBackOrder =
      variantMf.available_for_back_order === true ||
      variantMf.available_for_back_order === 'true';

    const patch = {};

    // Digital product remediation — only patch when we have both IDs.
    if ((isPlaySub || isStudioPro) && ids.fcID && ids.shopifyCustomerID) {
      if (isPlaySub && existing._playSub !== 'true') patch._playSub = 'true';
      if (isStudioPro && existing._studioPro !== 'true') patch._studioPro = 'true';
      if (!existing._fcID) patch._fcID = ids.fcID;
      if (!existing._shopifyCustomerID) patch._shopifyCustomerID = ids.shopifyCustomerID;
    }

    // Back-order remediation (checked before pre-order; a variant should be one or the other).
    if (isBackOrder) {
      const date = variantMf.back_order_available_date || '';
      const label = variantMf.backorder_cart_label || '';
      if (date) {
        if (existing._backOrder !== 'true') patch._backOrder = 'true';
        if (!existing._backOrderDate) patch._backOrderDate = date;
        if (label && !existing._backOrderLabel) patch._backOrderLabel = label;
        if (label && !existing[label]) patch[label] = date;
      }
    } else if (isPreOrder) {
      const date = variantMf.pre_order_available_date || '';
      const label = variantMf.preorder_cart_label || '';
      if (date) {
        if (existing._preOrder !== 'true') patch._preOrder = 'true';
        if (!existing._preOrderDate) patch._preOrderDate = date;
        if (label && !existing._preOrderLabel) patch._preOrderLabel = label;
        if (label && !existing[label]) patch[label] = date;
      }
    }

    return patch;
  }

  async function patchLineItem(lineIndex, mergedProperties) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ line: lineIndex, properties: mergedProperties })
    });
  }

  async function run() {
    if (inFlight) {
      pendingRerun = true;
      return;
    }
    inFlight = true;

    try {
      const [cartData, liveCart] = await Promise.all([fetchCartData(), fetchLiveCart()]);
      const ids = readJwtIds();
      const dataItems = (cartData && cartData.items) || [];
      const liveItems = (liveCart && liveCart.items) || [];
      if (!liveItems.length) return;

      let patched = false;

      for (let i = 0; i < liveItems.length; i++) {
        const cartItem = liveItems[i];
        const dataItem = dataItems.find(d => matchCartItem(cartItem, d));
        if (!dataItem) continue;

        const patch = buildRequiredProperties(cartItem, dataItem, ids);
        const patchKeys = Object.keys(patch);
        if (!patchKeys.length) continue;

        // /cart/change.js replaces the properties object, so merge with existing.
        const merged = Object.assign({}, cartItem.properties || {}, patch);

        try {
          await patchLineItem(i + 1, merged);
          patched = true;
        } catch (err) {
          console.error('[cart-property-sync] patch failed for line', i + 1, err);
        }
      }

      if (patched) {
        document.dispatchEvent(new CustomEvent('cart-property-sync:updated'));
      }
    } catch (err) {
      console.error('[cart-property-sync]', err);
    } finally {
      inFlight = false;
      if (pendingRerun) {
        pendingRerun = false;
        run();
      }
    }
  }

  function init() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        // Guard against the re-render loop: our patch call does not publish
        // cartUpdate itself, and we skip re-entry via `inFlight`.
        run();
      });
    }
    // Initial sweep for existing cart contents on page load.
    run();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
